var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Banner, FormField, FormGrid, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { analyticsApi } from 'apis/analytics';
import { analytics } from 'app/utils/segment';
import { HookFormCheckbox, HookFormText, HookFormTextarea, } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useCurrentUser } from 'utils/user/useCurrentUser';
import styles from './quickViewModal.module.scss';
const formId = 'analytics-quick-view-form';
/**
 * A modal what for creating, updating, and duplicating quick views in
 * Analytics.
 */
export const AnalyticsQuickViewModal = createAppModal({ type: 'analytics:quickView' }, ({ isOpen, modal, meta, hide }) => {
    var _a, _b;
    const { currentRole: { atLeastTeamlead }, } = useCurrentUser();
    const notify = useNotifications();
    const navigate = useAppNavigate();
    const analyticsState = useAppSelector((state) => state.analytics);
    const { mode = 'create', dashboardId, quickView, location } = modal.arg;
    // The dashboard object corresponding to the dashboardId.
    const dashboard = analyticsApi.endpoints.readDashboards.useQueryState(undefined, {
        selectFromResult: ({ data }) => {
            var _a;
            return (_a = data === null || data === void 0 ? void 0 : data.data.dashboards.find(({ id }) => id === dashboardId)) !== null && _a !== void 0 ? _a : analyticsState.dashboard;
        },
    });
    const [createQuickView, createState] = analyticsApi.useCreateQuickViewMutation();
    const [updateQuickView, updateState] = analyticsApi.useUpdateQuickViewMutation();
    const form = useForm({
        mode: 'all',
        defaultValues: {
            name: quickView
                ? mode === 'duplicate'
                    ? `${quickView.name} (copy)`
                    : quickView.name
                : '',
            description: (_a = quickView === null || quickView === void 0 ? void 0 : quickView.description) !== null && _a !== void 0 ? _a : '',
            shared: (_b = quickView === null || quickView === void 0 ? void 0 : quickView.shared) !== null && _b !== void 0 ? _b : false,
        },
    });
    /** Extracts the quick view params from the state. */
    const getParamsFromState = useCallback((dashboardId) => (Object.assign(Object.assign({}, pick(analyticsState, ['period', 'filters', 'settings'])), { context: analyticsState.context[dashboardId] })), [analyticsState]);
    /** Handles creating a new quick view. */
    const handleCreateQuickView = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d;
        if (!dashboardId)
            return;
        try {
            const params = getParamsFromState(dashboardId);
            const { data: { quick_view: createdQuickView }, } = yield createQuickView({
                dashboardId,
                quickView: Object.assign(Object.assign({}, values), { params }),
            }).unwrap();
            analytics.analyticsQuickViewCreated({
                dashboard: dashboard.title,
                filters: (_d = (_c = params.filters) === null || _c === void 0 ? void 0 : _c.map((filter) => filter.field)) !== null && _d !== void 0 ? _d : [],
                period: params.period.values.length === 1
                    ? params.period.values[0].value
                    : 'dateWithDefaultTime',
                shared: values.shared,
            });
            analytics.analyticsQuickViewSharingToggled({
                dashboard: dashboard.title,
                shared: values.shared,
            });
            notify({
                title: 'Success',
                text: `"${values.name}" has been added to the overview page.`,
                icon: 'i-done',
                time: 5000,
                leftAction: {
                    text: 'See overview page',
                    action: () => {
                        navigate({ path: '/analytics/quick-views' });
                    },
                },
            }, NotificationType.Success);
            navigate({
                path: '/analytics/dashboard/:slug/quick-view/:id',
                params: {
                    path: {
                        slug: dashboard.slug,
                        id: createdQuickView.id,
                    },
                },
            });
            hide === null || hide === void 0 ? void 0 : hide();
        }
        catch (error) {
            notify({
                title: 'Error while creating the quick view.',
            }, NotificationType.Error);
        }
    });
    /** Handles updating an existing quick view. */
    const handleUpdateQuickView = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _e, _f;
        if (!quickView)
            return;
        try {
            const params = Object.assign(Object.assign({}, quickView.params), getParamsFromState(quickView.dashboard_id));
            const { data: { quick_view: updatedQuickView }, } = yield updateQuickView({
                id: quickView.id,
                dashboardId: quickView.dashboard_id,
                quickView: Object.assign(Object.assign({}, values), { params }),
            }).unwrap();
            analytics.analyticsQuickViewUpdated({
                dashboard: dashboard.title,
                filters: (_f = (_e = params.filters) === null || _e === void 0 ? void 0 : _e.map((filter) => filter.field)) !== null && _f !== void 0 ? _f : [],
                period: params.period.values.length === 1
                    ? params.period.values[0].value
                    : 'dateWithDefaultTime',
                shared: values.shared,
            });
            // Track only if the sharing status has changed.
            if (quickView.shared !== values.shared) {
                analytics.analyticsQuickViewSharingToggled({
                    dashboard: dashboard.title,
                    shared: values.shared,
                });
            }
            meta === null || meta === void 0 ? void 0 : meta.resolve({ quickView: updatedQuickView });
            notify({
                title: 'Success',
                text: `Changes to "${values.name}" have been saved.`,
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            hide === null || hide === void 0 ? void 0 : hide();
        }
        catch (error) {
            notify({
                title: 'Error while updating the quick view.',
            }, NotificationType.Error);
        }
    });
    /** Handles creating a new quick view based on an existing one. */
    const handleDuplicateQuickView = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _g, _h;
        if (!quickView)
            return;
        try {
            const { params } = quickView;
            const { data: { quick_view: createdQuickView }, } = yield createQuickView({
                dashboardId: quickView.dashboard_id,
                quickView: Object.assign(Object.assign({}, values), { params }),
            }).unwrap();
            analytics.analyticsQuickViewDuplicated({
                dashboard: dashboard.title,
                filters: (_h = (_g = params.filters) === null || _g === void 0 ? void 0 : _g.map((filter) => filter.field)) !== null && _h !== void 0 ? _h : [],
                period: params.period.values.length === 1
                    ? params.period.values[0].value
                    : 'dateWithDefaultTime',
                shared: values.shared,
            });
            analytics.analyticsQuickViewSharingToggled({
                dashboard: dashboard.title,
                shared: values.shared,
            });
            notify({
                title: 'Success',
                text: `"${quickView.name}" has been copied as "${values.name}".`,
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            navigate({
                path: '/analytics/dashboard/:slug/quick-view/:id',
                params: {
                    path: {
                        slug: dashboard.slug,
                        id: createdQuickView.id,
                    },
                },
            });
            hide === null || hide === void 0 ? void 0 : hide();
        }
        catch (error) {
            notify({
                title: 'Error while copying the quick view.',
            }, NotificationType.Error);
        }
    });
    /** Handles valid form submit. */
    const handleValidSubmit = (values) => {
        if (mode === 'create') {
            handleCreateQuickView(values);
        }
        else if (mode === 'update') {
            handleUpdateQuickView(values);
        }
        else if (mode === 'duplicate') {
            handleDuplicateQuickView(values);
        }
    };
    /** Various texts according to the mode of the modal. */
    const texts = {
        create: {
            title: 'Create quick view',
            button: 'Create quick view',
        },
        duplicate: {
            title: 'Duplicate quick view',
            button: 'Duplicate quick view',
        },
        update: {
            title: 'Edit quick view',
            button: 'Save changes',
        },
    };
    return (_jsxs(Modal, Object.assign({ isOpen: isOpen, onClose: hide, className: styles.modal }, { children: [_jsx(ModalHead, { title: texts[mode].title, onClose: hide }), _jsx(ModalBody, { children: _jsx("form", Object.assign({ id: formId, noValidate: true, tabIndex: -1, onSubmit: form.handleSubmit(handleValidSubmit) }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(HookFormText, { name: "name", label: "Name", control: form.control, rules: {
                                        required: 'Name is required',
                                    }, componentProps: {
                                        autoFocus: true,
                                    } }) }), _jsx(FormField, { children: _jsx(HookFormTextarea, { name: "description", label: "Description", control: form.control, componentProps: {
                                        height: 102,
                                    } }) }), atLeastTeamlead && (_jsx(FormField, { children: _jsx(HookFormCheckbox, { name: "shared", label: "Make this quick view public", control: form.control, helpIcon: {
                                        dark: true,
                                        tooltipClassName: styles.helpTooltip,
                                        text: (_jsxs(Text.Paragraph, Object.assign({ variant: "2", color: "white" }, { children: ["Select the checkbox to make the view available from the ", _jsx("b", { children: "Quick views page" }), " for all users with Team Lead or Admin roles. You can always change this again later."] }))),
                                    } }) })), mode === 'update' && location !== 'quickViews' && (_jsx(FormField, { children: _jsx(Banner, { variant: "info", description: _jsxs(Text.Paragraph, Object.assign({ variant: "4" }, { children: ["Any changes made to the ", _jsx("b", { children: "period and filters" }), " will be saved."] })) }) }))] }) })) }), _jsx(ModalFoot, { cancelOnClick: hide, confirmName: texts[mode].button, confirmOnClick: noop, confirmProps: {
                    type: 'submit',
                    isLoading: createState.isLoading || updateState.isLoading,
                    disabled: !form.formState.isValid || form.formState.isSubmitting,
                    form: formId,
                } })] })));
});
